import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import loader from "../../../../methods/loader";
import ApiClient from "../../../../methods/api/apiClient";
import DateRangePicker from "../../../../components/common/DateRangePicker";

/* ─────────────────────────────────────────
   Tiny helpers
───────────────────────────────────────── */
const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : (n ?? 0));

const ArrowUp = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M7 11V3M7 3L3 7M7 3L11 7"
      stroke="#10B981"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const ArrowDown = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M7 3V11M7 11L3 7M7 11L11 7"
      stroke="#EF4444"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ExportIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10M11.333 5.333L8 2 4.667 5.333M8 2v8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect
      x="1"
      y="3"
      width="14"
      height="12"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path
      d="M1 7h14M5 1v4M11 1v4"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const SortIcon = ({ active, dir }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    style={{ opacity: active ? 1 : 0.35 }}
  >
    {dir === "asc" ? (
      <path
        d="M6 9V3M6 3L3 6M6 3L9 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <path
        d="M6 3V9M6 9L3 6M6 9L9 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

/* ─────────────────────────────────────────
   Stat Card
───────────────────────────────────────── */
const StatCard = ({ label, value, sub, icon, color, change }) => (
  <div className="stat-card" style={{ "--accent": color }}>
    <div className="stat-card__icon">{icon}</div>
    <div className="stat-card__body">
      <p className="stat-card__label">{label}</p>
      <h3 className="stat-card__value">{fmt(value)}</h3>
      {sub && <p className="stat-card__sub">{sub}</p>}
    </div>
    <div className="stat-card__stripe" />
  </div>
);

/* ─────────────────────────────────────────
   Period Toggle
───────────────────────────────────────── */
const periods = [
  { label: "24 h", value: "24h" },
  { label: "7 d", value: "7d" },
  { label: "30 d", value: "30d" },
  { label: "12 m", value: "12m" },
];

const PeriodToggle = ({ active, onChange }) => (
  <div className="period-toggle">
    {periods.map((p) => (
      <button
        key={p.value}
        type="button"
        className={`period-toggle__btn ${active === p.value ? "period-toggle__btn--active" : ""}`}
        onClick={() => onChange(p.value)}
      >
        {p.label}
      </button>
    ))}
  </div>
);

/* ─────────────────────────────────────────
   Bar Chart — Y-axis + hover tooltip
───────────────────────────────────────── */
const BarChart = ({
  data,
  labels,
  color1,
  color2,
  height = 220,
  chartLabel = "Value",
}) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  if (!data || data.length === 0) {
    return (
      <div
        style={{
          width: "100%",
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{ color: "#9CA3AF", fontSize: 13 }}>
          No data available
        </span>
      </div>
    );
  }

  const max = Math.max(...data, 1);
  const YAXIS_W = 36; // px for Y-axis label column
  const PAD_T = 12; // top padding
  const PAD_B = 32; // bottom for X labels
  const CHART_H = height - PAD_T - PAD_B;
  const SVG_W = 400;
  const PLOT_W = SVG_W - YAXIS_W;

  const yTicks = [0, 0.25, 0.5, 0.75, 1];
  const n = data.length;
  const barPct = 0.55;
  const barUnitW = PLOT_W / n;
  const barPxW = Math.min(36, barUnitW * barPct);
  const barOff = (barUnitW - barPxW) / 2;

  const shortLabel = (lbl) => {
    if (!lbl) return "";
    if (lbl.includes("T")) return lbl.split("T")[1];
    return lbl;
  };

  const handleMouseMove = (e, idx) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setHoveredIdx(idx);
    setTooltipPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const gradId = `grad-${color1.replace("#", "")}`;
  const hoverGradId = `grad-h-${color1.replace("#", "")}`;

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height,
        position: "relative",
        userSelect: "none",
      }}
    >
      {/* Tooltip */}
      {hoveredIdx !== null && (
        <div
          className="chart-tooltip"
          style={{
            left: Math.min(
              tooltipPos.x + 14,
              (containerRef.current?.offsetWidth ?? 400) - 110,
            ),
            top: Math.max(tooltipPos.y - 46, 0),
          }}
        >
          <span className="chart-tooltip__label">
            {shortLabel(labels[hoveredIdx])}
          </span>
          <span className="chart-tooltip__value">
            {chartLabel}: {data[hoveredIdx]}
          </span>
        </div>
      )}

      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${SVG_W} ${height}`}
        preserveAspectRatio="none"
        style={{ overflow: "visible", display: "block" }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color1} />
            <stop offset="100%" stopColor={color2} />
          </linearGradient>
          <linearGradient id={hoverGradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color1} stopOpacity="0.85" />
            <stop offset="100%" stopColor={color2} stopOpacity="0.6" />
          </linearGradient>
        </defs>

        {/* Y-axis grid + labels */}
        {yTicks.map((frac) => {
          const yPx = PAD_T + (1 - frac) * CHART_H;
          const tickVal = Math.round(max * frac);
          return (
            <g key={frac}>
              <line
                x1={YAXIS_W}
                y1={yPx}
                x2={SVG_W}
                y2={yPx}
                stroke={frac === 0 ? "#D1D5DB" : "#F0F0F0"}
                strokeDasharray={frac === 0 ? "0" : "4 4"}
                strokeWidth="1"
              />
              <text
                x={YAXIS_W - 6}
                y={yPx + 4}
                textAnchor="end"
                fontSize="9"
                fill="#C0C0C0"
              >
                {tickVal >= 1000 ? `${(tickVal / 1000).toFixed(1)}k` : tickVal}
              </text>
            </g>
          );
        })}

        {/* Y-axis vertical rule */}
        <line
          x1={YAXIS_W}
          y1={PAD_T}
          x2={YAXIS_W}
          y2={PAD_T + CHART_H}
          stroke="#E5E7EB"
          strokeWidth="1"
        />

        {/* Bars */}
        {data.map((val, i) => {
          const barH = (val / max) * CHART_H;
          const xLeft = YAXIS_W + i * barUnitW + barOff;
          const yTop = PAD_T + CHART_H - barH;
          const isHov = hoveredIdx === i;

          return (
            <g key={i}>
              {/* Invisible full-height hover zone */}
              <rect
                x={xLeft - 4}
                y={PAD_T}
                width={barPxW + 8}
                height={CHART_H}
                fill="transparent"
                style={{ cursor: "pointer" }}
                onMouseMove={(e) => handleMouseMove(e, i)}
                onMouseLeave={() => setHoveredIdx(null)}
              />

              {/* Bar */}
              <rect
                x={xLeft}
                y={yTop}
                width={barPxW}
                height={barH}
                fill={isHov ? `url(#${hoverGradId})` : `url(#${gradId})`}
                rx="3"
                style={{
                  filter: isHov ? `drop-shadow(0 3px 8px ${color1}66)` : "none",
                  transition: "filter 0.12s",
                  pointerEvents: "none",
                }}
              />

              {/* Value label on hover */}
              {isHov && (
                <text
                  x={xLeft + barPxW / 2}
                  y={yTop - 5}
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="700"
                  fill={color1}
                  style={{ pointerEvents: "none" }}
                >
                  {val}
                </text>
              )}

              {/* X-axis label */}
              <text
                x={xLeft + barPxW / 2}
                y={PAD_T + CHART_H + 16}
                textAnchor="middle"
                fontSize="9"
                fill={isHov ? "#6B7280" : "#9CA3AF"}
                fontWeight={isHov ? "600" : "400"}
                style={{ pointerEvents: "none" }}
              >
                {shortLabel(labels[i])}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/* ─────────────────────────────────────────
   Main Component
───────────────────────────────────────── */
const Analytics = () => {
  const user = useSelector((state) => state.user);
  const userId = user?.id || user?._id;

  /* ── Period states (independent per section) ── */
  const [summaryPeriod, setSummaryPeriod] = useState("7d");
  const [visitorPeriod, setVisitorPeriod] = useState("7d");
  const [chatPeriod, setChatPeriod] = useState("7d");
  const [trafficPeriod, setTrafficPeriod] = useState("7d");

  /* ── Visitor Analytics date range ── */
  const today = new Date().toISOString().split("T")[0];
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0];
  const [vaFrom, setVaFrom] = useState(monthAgo);
  const [vaTo, setVaTo] = useState(today);

  /* ── API data ── */
  const [summaryData, setSummaryData] = useState(null);
  const [visitorTrend, setVisitorTrend] = useState({ labels: [], data: [] });
  const [chatActivity, setChatActivity] = useState({ labels: [], data: [] });
  const [trafficData, setTrafficData] = useState([]);
  const [visitorAnalytics, setVisitorAnalytics] = useState([]);

  /* ── Loading flags ── */
  const [loadingVisitor, setLoadingVisitor] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [loadingTraffic, setLoadingTraffic] = useState(false);
  const [loadingVA, setLoadingVA] = useState(false);

  /* ── Sorting ── */
  const [visitorsSort, setVisitorsSort] = useState({
    col: "date",
    dir: "desc",
  });
  const [urlSort, setUrlSort] = useState({ col: "visitors", dir: "desc" });

  /* ────────────────────────────────────────
     API Fetchers
  ─────────────────────────────────────── */
  const GetSummaryData = useCallback(
    (period = summaryPeriod) => {
      if (!userId) return;
      ApiClient.get("analytics/summary", { userId, period }).then((res) => {
        if (res?.success && res.data) setSummaryData(res.data);
      });
    },
    [userId, summaryPeriod],
  );

  const GetVisitorTrends = useCallback(
    (period = visitorPeriod) => {
      if (!userId) return;
      setLoadingVisitor(true);
      ApiClient.get("analytics/visitor-trends", { userId, period })
        .then((res) => {
          if (res?.success)
            setVisitorTrend({ labels: res.labels || [], data: res.data || [] });
          setLoadingVisitor(false);
        })
        .catch(() => setLoadingVisitor(false));
    },
    [userId, visitorPeriod],
  );

  const GetChatAnalytics = useCallback(
    (period = chatPeriod) => {
      if (!userId) return;
      setLoadingChat(true);
      ApiClient.get("analytics/chat-activity", { userId, period })
        .then((res) => {
          if (res?.success)
            setChatActivity({ labels: res.labels || [], data: res.data || [] });
          setLoadingChat(false);
        })
        .catch(() => setLoadingChat(false));
    },
    [userId, chatPeriod],
  );

  const GetTrafficAnalytics = useCallback(
    (period = trafficPeriod) => {
      if (!userId) return;
      setLoadingTraffic(true);
      ApiClient.get("analytics/traffic-by-url", { userId, period })
        .then((res) => {
          if (res?.success && Array.isArray(res.data)) setTrafficData(res.data);
          setLoadingTraffic(false);
        })
        .catch(() => setLoadingTraffic(false));
    },
    [userId, trafficPeriod],
  );

  const GetVisitorAnalytics = useCallback(
    (from = vaFrom, to = vaTo) => {
      if (!userId) return;
      setLoadingVA(true);
      ApiClient.get("analytics/visitor-analytics", {
        userId,
        page: 1,
        count: 1000,
        from,
        to,
      })
        .then((res) => {
          if (res?.success) {
            const rows = Array.isArray(res.data) ? res.data : [];
            setVisitorAnalytics(rows);
          }
          setLoadingVA(false);
        })
        .catch(() => setLoadingVA(false));
    },
    [userId, vaFrom, vaTo],
  );

  /* ── Initial load ── */
  useEffect(() => {
    GetSummaryData();
    GetVisitorTrends();
    GetChatAnalytics();
    GetTrafficAnalytics();
    GetVisitorAnalytics();
  }, []);

  /* ── Period handlers ── */
  const handleSummaryPeriod = (p) => {
    setSummaryPeriod(p);
    GetSummaryData(p);
  };
  const handleVisitorPeriod = (p) => {
    setVisitorPeriod(p);
    GetVisitorTrends(p);
  };
  const handleChatPeriod = (p) => {
    setChatPeriod(p);
    GetChatAnalytics(p);
  };
  const handleTrafficPeriod = (p) => {
    setTrafficPeriod(p);
    GetTrafficAnalytics(p);
  };

  /* ── Date range handlers ── */
  const handleVaFrom = (e) => {
    const val = e.target.value;
    setVaFrom(val);
    GetVisitorAnalytics(val, vaTo);
  };
  const handleVaTo = (e) => {
    const val = e.target.value;
    setVaTo(val);
    GetVisitorAnalytics(vaFrom, val);
  };

  /* ── Export CSV ── */
  const exportCSV = (rows, filename) => {
    if (!rows?.length) return;
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) =>
        headers.map((k) => JSON.stringify(r[k] ?? "")).join(","),
      ),
    ].join("\n");
    const a = Object.assign(document.createElement("a"), {
      href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })),
      download: filename,
    });
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  /* ── Sort helpers ── */
  const toggleSort = (setter, col) =>
    setter((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "desc" },
    );

  const sortedVisitors = [...visitorAnalytics]?.sort((a, b) => {
    if (visitorsSort?.col === "date" || visitorsSort?.col === "month") {
      const diff =
        new Date(a?.date || a?.month || 0) - new Date(b?.date || b?.month || 0);
      return visitorsSort.dir === "asc" ? diff : -diff;
    }
    const diff = (a[visitorsSort.col] ?? 0) - (b[visitorsSort?.col] ?? 0);
    return visitorsSort.dir === "asc" ? diff : -diff;
  });

  const sortedUrls = [...trafficData]?.sort((a, b) => {
    const diff = (a[urlSort.col] ?? 0) - (b[urlSort.col] ?? 0);
    return urlSort.dir === "asc" ? diff : -diff;
  });

  /* ── Cards config ── */
  const cards = [
    {
      label: "Total Visitors",
      value: summaryData?.totalVisitors?.value ?? 0,
      color: "#6366F1",
      change: summaryData?.totalVisitors?.changePercent ?? undefined,
      icon: (
        <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
          <path
            d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      label: "Visitors Who Chatted",
      value: summaryData?.visitorsWhoChatted?.value ?? 0,
      color: "#10B981",
      change: summaryData?.visitorsWhoChatted?.changePercent ?? undefined,
      icon: (
        <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
          <path
            d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      label: "Visitors Without Chat",
      value: summaryData?.visitorsWithoutChat?.value ?? 0,
      color: "#F59E0B",
      change: summaryData?.visitorsWithoutChat?.changePercent ?? undefined,
      icon: (
        <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M8 15s1.5-2 4-2 4 2 4 2M9 9h.01M15 9h.01"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      label: "Total Messages",
      value: summaryData?.totalMessages?.value ?? 0,
      color: "#8B5CF6",
      change: summaryData?.totalMessages?.changePercent ?? undefined,
      icon: (
        <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
          <path
            d="M8 9h8M8 13h5M20 2H4a2 2 0 00-2 2v12a2 2 0 002 2h4l4 4 4-4h4a2 2 0 002-2V4a2 2 0 00-2-2z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  ];

  /* ── Visitor analytics table columns ── */
  const visitorCols = [
    { key: "date", label: "Date", sortable: true },
    { key: "visitors", label: "Visitors", sortable: true },
    { key: "chats", label: "Chats", sortable: true },
    { key: "messages", label: "Messages", sortable: true },
    { key: "conversionPct", label: "Conversion %", sortable: true },
  ];

  const handleDatePickerChange = (e) => {
    if (e?.startDate) {
      setVaFrom(e?.startDate);
    }
    if (e?.endDate) {
      setVaTo(e?.endDate);
    }
    if (e?.startDate && e?.endDate) {
      GetVisitorAnalytics(e?.startDate, e?.endDate);
    }
  };
  return (
    <>
      <style>{CSS}</style>
      <div className="analytics-root">
        {/* ── Stats Cards ── */}
        <div className="analytics-section-header">
          <h4 className="analytics-section-title">Overview</h4>
          <PeriodToggle active={summaryPeriod} onChange={handleSummaryPeriod} />
        </div>
        <div className="analytics-cards">
          {cards.map((c) => (
            <StatCard key={c.label} {...c} />
          ))}
        </div>

        {/* ── Charts Row ── */}
        <div className="analytics-charts-row">
          <div className="analytics-chart-card">
            <div className="analytics-chart-card__header">
              <div>
                <h4 className="analytics-section-title">Visitor Trends</h4>
                <p className="analytics-section-sub">
                  Unique visitors over time
                </p>
              </div>
              <PeriodToggle
                active={visitorPeriod}
                onChange={handleVisitorPeriod}
              />
            </div>
            {loadingVisitor ? (
              <div className="chart-loading">Loading…</div>
            ) : (
              <BarChart
                data={visitorTrend.data}
                labels={visitorTrend.labels}
                color1="#818CF8"
                color2="#115B9D"
                chartLabel="Visitors"
              />
            )}
          </div>

          <div className="analytics-chart-card">
            <div className="analytics-chart-card__header">
              <div>
                <h4 className="analytics-section-title">Chat Activity</h4>
                <p className="analytics-section-sub">
                  Conversations initiated over time
                </p>
              </div>
              <PeriodToggle active={chatPeriod} onChange={handleChatPeriod} />
            </div>
            {loadingChat ? (
              <div className="chart-loading">Loading…</div>
            ) : (
              <BarChart
                data={chatActivity.data}
                labels={chatActivity.labels}
                color1="#34D399"
                color2="#059669"
                chartLabel="Chats"
              />
            )}
          </div>
        </div>

        {/* ── Bottom Row ── */}
        <div className="analytics-bottom-row">
          {/* Visitor Analytics Table */}
          <div className="analytics-table-card">
            <div className="analytics-table-card__header">
              <div>
                <h4 className="analytics-section-title">Visitor Analytics</h4>
                <p className="analytics-section-sub">
                  Detailed breakdown by date range
                </p>
              </div>
              <div className="analytics-table-card__actions">
                <DateRangePicker
                  value={{
                    startDate: vaFrom,
                    endDate: vaTo,
                  }}
                  isCompare={false}
                  onChange={(e) => {
                    handleDatePickerChange(e);
                  }}
                  fullWidth={true}
                  monthsCount={1}
                />
                <button
                  type="button"
                  className="btn-export"
                  onClick={() =>
                    exportCSV(visitorAnalytics, "visitor-analytics.csv")
                  }
                >
                  <ExportIcon /> Export
                </button>
              </div>
            </div>

            {/* ================= Desktop Table ================= */}
            <div className="hidden md:block table-wrap overflow-x-hidden">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    {visitorCols.map((col) => (
                      <th
                        key={col.key}
                        className={col.sortable ? "sortable" : ""}
                        onClick={() =>
                          col.sortable && toggleSort(setVisitorsSort, col.key)
                        }
                      >
                        {col.label}
                        {col.sortable && (
                          <SortIcon
                            active={visitorsSort.col === col.key}
                            dir={
                              visitorsSort.col === col.key
                                ? visitorsSort.dir
                                : "desc"
                            }
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {loadingVA ? (
                    <tr>
                      <td colSpan={5} className="table-empty">
                        Loading…
                      </td>
                    </tr>
                  ) : sortedVisitors.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="table-empty">
                        No data available
                      </td>
                    </tr>
                  ) : (
                    sortedVisitors.map((row, i) => {
                      const rawDate = row.date || row.month || "";
                      const d = rawDate ? new Date(rawDate) : null;

                      const label =
                        d && !isNaN(d)
                          ? d.toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : rawDate;

                      const conversion =
                        row.conversion ??
                        (row.visitors
                          ? Math.round(((row.chats ?? 0) / row.visitors) * 100)
                          : 0);

                      return (
                        <tr key={i}>
                          <td className="td-date">{label}</td>

                          <td>
                            <span className="td-badge td-badge--indigo">
                              {fmt(row.visitors)}
                            </span>
                          </td>

                          <td>
                            <span className="td-badge td-badge--emerald">
                              {fmt(row.chats)}
                            </span>
                          </td>

                          <td>{fmt(row.messages ?? row.totalMessages ?? 0)}</td>

                          <td>
                            <div className="td-conversion">
                              <div className="td-conversion__bar">
                                <div
                                  className="td-conversion__fill"
                                  style={{
                                    width: `${Math.min(conversion, 100)}%`,
                                  }}
                                />
                              </div>

                              <span>{conversion}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ================= Mobile Cards ================= */}
            <div className="block md:hidden space-y-4">
              {loadingVA ? (
                <div className="text-center py-6 text-gray-500">Loading...</div>
              ) : sortedVisitors.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  No data available
                </div>
              ) : (
                sortedVisitors.map((row, i) => {
                  const rawDate = row.date || row.month || "";
                  const d = rawDate ? new Date(rawDate) : null;

                  const label =
                    d && !isNaN(d)
                      ? d.toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : rawDate;

                  const conversion =
                    row.conversion ??
                    (row.visitors
                      ? Math.round(((row.chats ?? 0) / row.visitors) * 100)
                      : 0);

                  return (
                    <div
                      key={i}
                      className="bg-white border rounded-xl shadow-sm p-4 space-y-3"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 text-sm">Date</span>

                        <span className="font-semibold">{label}</span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 text-sm">Visitors</span>

                        <span className="td-badge td-badge--indigo">
                          {fmt(row.visitors)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 text-sm">Chats</span>

                        <span className="td-badge td-badge--emerald">
                          {fmt(row.chats)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 text-sm">Messages</span>

                        <span>
                          {fmt(row.messages ?? row.totalMessages ?? 0)}
                        </span>
                      </div>

                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-500">Conversion</span>

                          <span className="font-medium">{conversion}%</span>
                        </div>

                        <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
                          <div
                            className="h-full bg-indigo-600 rounded-full"
                            style={{
                              width: `${Math.min(conversion, 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Traffic by URL Table */}
          <div className="analytics-table-card analytics-table-card--url">
            <div className="analytics-table-card__header">
              <div>
                <h4 className="analytics-section-title">Traffic by URL</h4>
                <p className="analytics-section-sub">
                  Top pages driving visitor traffic
                </p>
              </div>
              <div className="analytics-table-card__actions">
                <PeriodToggle
                  active={trafficPeriod}
                  onChange={handleTrafficPeriod}
                />
                <button
                  type="button"
                  className="btn-export"
                  onClick={() => exportCSV(trafficData, "url-analytics.csv")}
                >
                  <ExportIcon /> Export
                </button>
              </div>
            </div>

            {/* <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Page URL</th>
                    <th className="sortable" onClick={() => toggleSort(setUrlSort, "visitors")}>
                      Visitors <SortIcon active={urlSort.col === "visitors"} dir={urlSort.col === "visitors" ? urlSort.dir : "desc"} />
                    </th>
                    <th className="sortable" onClick={() => toggleSort(setUrlSort, "chats")}>
                      Chats <SortIcon active={urlSort.col === "chats"} dir={urlSort.col === "chats" ? urlSort.dir : "desc"} />
                    </th>
                    <th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTraffic ? (
                    <tr><td colSpan={4} className="table-empty">Loading…</td></tr>
                  ) : sortedUrls.length === 0 ? (
                    <tr><td colSpan={4} className="table-empty">No URL data available</td></tr>
                  ) : (
                    sortedUrls.map((row, i) => (
                      <tr key={i}>
                        <td className="td-url" title={row.url}>
                          <span className="td-url__rank">{row.rank ?? i + 1}</span>
                          <span className="td-url__text">{row.url}</span>
                        </td>
                        <td><span className="td-badge td-badge--indigo">{fmt(row.visitors)}</span></td>
                        <td><span className="td-badge td-badge--emerald">{fmt(row.chats)}</span></td>
                        <td>
                          <div className="td-conversion">
                            <div className="td-conversion__bar">
                              <div
                                className="td-conversion__fill td-conversion__fill--purple"
                                style={{ width: `${Math.min(row.sharePct ?? 0, 100)}%` }}
                              />
                            </div>
                            <span>{row.sharePct ?? 0}%</span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div> */}

            {/* ================= Desktop Table ================= */}
            <div className="hidden md:block table-wrap overflow-x-hidden">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th>Page URL</th>

                    <th
                      className="sortable"
                      onClick={() => toggleSort(setUrlSort, "visitors")}
                    >
                      Visitors
                      <SortIcon
                        active={urlSort.col === "visitors"}
                        dir={urlSort.col === "visitors" ? urlSort.dir : "desc"}
                      />
                    </th>

                    <th
                      className="sortable"
                      onClick={() => toggleSort(setUrlSort, "chats")}
                    >
                      Chats
                      <SortIcon
                        active={urlSort.col === "chats"}
                        dir={urlSort.col === "chats" ? urlSort.dir : "desc"}
                      />
                    </th>

                    <th>Share</th>
                  </tr>
                </thead>

                <tbody>
                  {loadingTraffic ? (
                    <tr>
                      <td colSpan={4} className="table-empty">
                        Loading…
                      </td>
                    </tr>
                  ) : sortedUrls.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="table-empty">
                        No URL data available
                      </td>
                    </tr>
                  ) : (
                    sortedUrls.map((row, i) => (
                      <tr key={i}>
                        <td className="td-url" title={row.url}>
                          <span className="td-url__rank">
                            {row.rank ?? i + 1}
                          </span>

                          <span className="td-url__text">{row.url}</span>
                        </td>

                        <td>
                          <span className="td-badge td-badge--indigo">
                            {fmt(row.visitors)}
                          </span>
                        </td>

                        <td>
                          <span className="td-badge td-badge--emerald">
                            {fmt(row.chats)}
                          </span>
                        </td>

                        <td>
                          <div className="td-conversion">
                            <div className="td-conversion__bar">
                              <div
                                className="td-conversion__fill td-conversion__fill--purple"
                                style={{
                                  width: `${Math.min(row.sharePct ?? 0, 100)}%`,
                                }}
                              />
                            </div>

                            <span>{row.sharePct ?? 0}%</span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* ================= Mobile Cards ================= */}
            <div className="block md:hidden space-y-4">
              {loadingTraffic ? (
                <div className="text-center py-6 text-gray-500">Loading...</div>
              ) : sortedUrls.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  No URL data available
                </div>
              ) : (
                sortedUrls.map((row, i) => (
                  <div
                    key={i}
                    className="bg-white border rounded-xl shadow-sm p-4 space-y-4"
                  >
                    {/* URL */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Page URL</p>

                      <div className="flex items-start gap-2">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 font-semibold text-sm">
                          {row.rank ?? i + 1}
                        </span>

                        <span className="break-all text-sm font-medium text-gray-800">
                          {row.url}
                        </span>
                      </div>
                    </div>

                    {/* Visitors */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Visitors</span>

                      <span className="td-badge td-badge--indigo">
                        {fmt(row.visitors)}
                      </span>
                    </div>

                    {/* Chats */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Chats</span>

                      <span className="td-badge td-badge--emerald">
                        {fmt(row.chats)}
                      </span>
                    </div>

                    {/* Share */}
                    <div>
                      <div className="flex justify-between mb-2 text-sm">
                        <span className="text-gray-500">Share</span>

                        <span className="font-medium">
                          {row.sharePct ?? 0}%
                        </span>
                      </div>

                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-purple-600"
                          style={{
                            width: `${Math.min(row.sharePct ?? 0, 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────
   Scoped CSS
───────────────────────────────────────── */
const CSS = `
.analytics-root {
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 1400px;
  font-family: 'Inter', 'DM Sans', sans-serif;
}

/* ── Section header row ── */
.analytics-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

/* ── Cards ── */
.analytics-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
@media (max-width: 1024px) { .analytics-cards { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px)  { .analytics-cards { grid-template-columns: 1fr; } }

.stat-card {
  position: relative;
  overflow: hidden;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  padding: 20px 20px 18px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  transition: box-shadow 0.18s, transform 0.18s;
}
.stat-card:hover {
  box-shadow: 0 8px 24px rgba(0,0,0,0.07);
  transform: translateY(-2px);
}
.stat-card__stripe {
  position: absolute;
  top: 0; left: 0;
  width: 4px; height: 100%;
  background: var(--accent, #6366F1);
  border-radius: 4px 0 0 4px;
}
.stat-card__icon {
  width: 40px; height: 40px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--accent, #6366F1) 12%, white);
  color: var(--accent, #6366F1);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.stat-card__body { flex: 1; }
.stat-card__label {
  font-size: 12px; font-weight: 500; color: #6B7280;
  margin: 0 0 4px; text-transform: uppercase; letter-spacing: 0.04em;
}
.stat-card__value {
  font-size: 22px; font-weight: 700; color: #5d5d5d;
  margin: 0 0 6px; line-height: 1;
}
.stat-card__badge {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 11px; font-weight: 500; padding: 2px 7px; border-radius: 99px;
}
.stat-card__badge--up   { background: #ECFDF5; color: #059669; }
.stat-card__badge--down { background: #FEF2F2; color: #DC2626; }
.stat-card__sub { font-size: 11px; color: #9CA3AF; margin: 4px 0 0; }

/* ── Chart cards ── */
.analytics-charts-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
@media (max-width: 900px) { .analytics-charts-row { grid-template-columns: 1fr; } }

.analytics-chart-card {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  padding: 20px 20px 12px;
}
.analytics-chart-card__header {
  display: flex; align-items: flex-start;
  justify-content: space-between; gap: 12px; margin-bottom: 16px;
      flex-wrap: wrap;
}
.analytics-section-title { font-size: 15px; font-weight: 600; color: #111827; margin: 0 0 2px; }
.analytics-section-sub   { font-size: 12px; color: #9CA3AF; margin: 0; }

.chart-loading {
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
  font-size: 13px;
}

/* ── Chart tooltip ── */
.chart-tooltip {
  position: absolute;
  background: #1F2937;
  color: #fff;
  border-radius: 8px;
  padding: 7px 11px;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  z-index: 50;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 4px 14px rgba(0,0,0,0.22);
}
.chart-tooltip__label { font-size: 10px; color: #9CA3AF; }
.chart-tooltip__value { font-size: 13px; font-weight: 700; color: #fff; }

/* ── Period Toggle ── */
.period-toggle {
  display: flex; background: #F3F4F6;
  border-radius: 8px; padding: 3px; gap: 2px; flex-shrink: 0;
}
.period-toggle__btn {
  padding: 5px 14px; font-size: 12px; font-weight: 500;
  color: #6B7280; border: none; background: transparent;
  border-radius: 6px; cursor: pointer;
  transition: background 0.15s, color 0.15s, box-shadow 0.15s; white-space: nowrap;
}
.period-toggle__btn--active {
  background: #fff; color: #111827;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
}

/* ── Bottom row ── */
.analytics-bottom-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 16px;
}
@media (max-width: 1024px) { .analytics-bottom-row { grid-template-columns: 1fr; } }

.analytics-table-card {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  overflow: hidden;
}
.analytics-table-card__header {
  display: flex; align-items: flex-start;
  justify-content: space-between; flex-wrap: wrap;
  gap: 10px; padding: 18px 20px 14px;
  border-bottom: 1px solid #F3F4F6;
}
.analytics-table-card__actions {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}

/* ── Date range picker ── */
.date-range-picker {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  color: #6B7280;
  transition: border-color 0.15s;
}
.date-range-picker:focus-within {
  border-color: #6366F1;
  box-shadow: 0 0 0 3px rgba(99,102,241,0.08);
}
.date-input {
  border: none;
  background: transparent;
  font-size: 12px;
  color: #374151;
  outline: none;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
  min-width: 108px;
}
.date-input::-webkit-calendar-picker-indicator {
  opacity: 0.45;
  cursor: pointer;
}
.date-range-sep {
  font-size: 11px;
  color: #9CA3AF;
  flex-shrink: 0;
}

/* ── Export button ── */
.btn-export {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px; font-size: 13px; font-weight: 500;
  color: #374151; background: #F9FAFB;
  border: 1px solid #E5E7EB; border-radius: 8px;
  cursor: pointer; transition: background 0.15s, border-color 0.15s; white-space: nowrap;
}
.btn-export:hover { background: #F3F4F6; border-color: #D1D5DB; }

/* ── Table ── */
.table-wrap { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.data-table thead th {
  padding: 10px 16px; text-align: left;
  font-size: 11px; font-weight: 600; color: #6B7280;
  text-transform: uppercase; letter-spacing: 0.05em;
  background: #F9FAFB; border-bottom: 1px solid #F3F4F6;
  white-space: nowrap;
}
.data-table thead th.sortable { cursor: pointer; user-select: none; }
.data-table thead th.sortable:hover { color: #374151; }
.data-table thead th svg { display: inline; margin-left: 4px; vertical-align: middle; }
.data-table tbody tr { border-bottom: 1px solid #F9FAFB; transition: background 0.1s; }
.data-table tbody tr:last-child { border-bottom: none; }
.data-table tbody tr:hover { background: #FAFAFA; }
.data-table tbody td { padding: 11px 16px; color: #374151; vertical-align: middle; }
.table-empty { text-align: center; color: #9CA3AF; padding: 40px !important; font-size: 13px; }

/* ── Cell types ── */
.td-date { color: #6B7280; white-space: nowrap; font-size: 12px; }
.td-badge {
  display: inline-flex; align-items: center;
  padding: 2px 9px; border-radius: 99px;
  font-size: 12px; font-weight: 600;
}
.td-badge--indigo  { background: #EEF2FF; color: #115B9D; }
.td-badge--emerald { background: #ECFDF5; color: #059669; }
.td-conversion { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #6B7280; }
.td-conversion__bar {
  flex: 1; height: 6px; background: #F3F4F6;
  border-radius: 99px; overflow: hidden; min-width: 50px;
}
.td-conversion__fill {
  height: 100%;
  background: linear-gradient(90deg, #6366F1, #818CF8);
  border-radius: 99px; transition: width 0.4s ease;
}
.td-conversion__fill--purple { background: linear-gradient(90deg, #8B5CF6, #A78BFA); }
.td-url { display: flex; align-items: center; gap: 8px; max-width: 220px; }
.td-url__rank {
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; font-size: 10px; font-weight: 700;
  color: #9CA3AF; background: #F3F4F6; border-radius: 5px; flex-shrink: 0;
}
.td-url__text {
  overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; font-size: 12px; color: #115B9D;
}
`;

export default Analytics;
